import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import SwiperCore, { Thumbs } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import I18n from 'lib/i18n'
import railsContext from 'lib/selectors/rails-context'

SwiperCore.use([Thumbs])

export default function ClassCarousel ({
  closeLightBox,
  currentImage,
  currentImageNumber,
  lightBoxPhotos,
  setCurrentImage
}) {
  const { i18nLocale } = useSelector(railsContext)
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const mainSwiperRef = useRef(null)
  const lightboxRef = useRef(null)

  const isRtl = i18nLocale === 'ar'
  const isLargeScreen = useMediaQuery({ minWidth: 1008 })

  const handleGoToPrevious = event => {
    if (event) {
      event.stopPropagation()
    }
    if (mainSwiperRef.current && mainSwiperRef.current.swiper) {
      mainSwiperRef.current.swiper.slidePrev()
    }
  }

  const handleGoToNext = event => {
    if (event) {
      event.stopPropagation()
    }
    if (mainSwiperRef.current && mainSwiperRef.current.swiper) {
      mainSwiperRef.current.swiper.slideNext()
    }
  }

  const handleBackdropClick = event => {
    const target = event.target
    const isImage = target.matches('img.main-image')
    const isCaption = target.matches('span.image-caption')
    const isThumb = target.matches('img.thumbnail-image')
    const isNav = !!target.closest('.navigation')
    const isThumbSection = !!target.closest('.swiper-wrapper')

    if (isImage || isCaption || isThumb || isNav || isThumbSection) return

    closeLightBox()
  }

  useEffect(() => {
    const handleKeyDown = event => {
      switch (event.key) {
        case 'ArrowLeft':
          if (!isRtl) {
            event.preventDefault()
            handleGoToPrevious()
          } else {
            event.preventDefault()
            handleGoToNext()
          }
          break
        case 'ArrowRight':
          if (!isRtl) {
            event.preventDefault()
            handleGoToNext()
          } else {
            event.preventDefault()
            handleGoToPrevious()
          }
          break
        case 'Escape':
          event.preventDefault()
          closeLightBox()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isRtl, closeLightBox])

  useEffect(() => {
    const lightboxElement = lightboxRef.current
    if (!lightboxElement) return

    const preventMultiTouch = event => {
      if (event.touches && event.touches.length > 1) {
        event.preventDefault()
      }
    }

    const preventGesture = event => {
      event.preventDefault()
    }

    lightboxElement.addEventListener('touchstart', preventMultiTouch, { passive: false })
    lightboxElement.addEventListener('touchmove', preventMultiTouch, { passive: false })
    lightboxElement.addEventListener('gesturestart', preventGesture)
    lightboxElement.addEventListener('gesturechange', preventGesture)
    lightboxElement.addEventListener('gestureend', preventGesture)

    return () => {
      lightboxElement.removeEventListener('touchstart', preventMultiTouch)
      lightboxElement.removeEventListener('touchmove', preventMultiTouch)
      lightboxElement.removeEventListener('gesturestart', preventGesture)
      lightboxElement.removeEventListener('gesturechange', preventGesture)
      lightboxElement.removeEventListener('gestureend', preventGesture)
    }
  }, [])

  const handleClick = event => {
    event.stopPropagation()
    closeLightBox()
  }

  return (
    <div
      className='lightbox-wrapper'
      ref={lightboxRef}
      role='dialog'
      aria-label='Image gallery'
      onClick={handleBackdropClick}
    >
      <div className='close-button-wrapper'>
        <div className='close-button' role='button' onClick={handleClick}>
          <span className='close-text'>{I18n.t('bookings.edit_payment.gift_card_close')}</span>
          <img src='/assets/icons/close-icon.svg' alt='close icon' />
        </div>
      </div>

      <div className={`navigation-wrapper${isRtl ? ' right-arrow' : ' left-arrow'}`}>
        <span
          className={`navigation previous${currentImageNumber === 0 ? ' hidden' : ''}`}
          role='button'
          onClick={handleGoToPrevious}
        >
          <img src={`/assets/icons/${isRtl ? 'right-arrow-icon.svg' : 'left-arrow-icon.svg'}`} alt='previous' />
        </span>
      </div>

      <div className='lightbox-content'>
        <Swiper
          ref={mainSwiperRef}
          initialSlide={currentImage}
          spaceBetween={0}
          slidesPerView={1}
          centeredSlides
          allowTouchMove={!isLargeScreen}
          onSlideChange={({ activeIndex }) => setCurrentImage(activeIndex)}
          thumbs={{ swiper: thumbsSwiper }}
          className='main-swiper'
          keyboard={{
            enabled: true,
            onlyInViewport: false,
            pageUpDown: true
          }}
          grabCursor={true}
        >
          {lightBoxPhotos.map((photo, index) => (
            <SwiperSlide zoom={false} key={index}>
              <div className='main-swiper-image' key={index}>
                <div className='image-caption-wrapper'>
                  <span className='image-caption'>{photo.caption}</span>
                </div>
                <div className='swiper-image-wrapper'>
                  <img src={photo.src} alt={photo.caption || `Image ${index + 1}`} className='main-image' />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className='swiper-footer'>
          <div className='carousel-status'>
            {currentImage + 1} / {lightBoxPhotos.length}
          </div>

          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={5}
            slidesPerView={5}
            freeMode
            watchSlidesVisibility
            watchSlidesProgress
            className='thumbs-swiper'
            height={60}
          >
            {lightBoxPhotos.map((photo, index) => (
              <SwiperSlide zoom={false} key={index}>
                <div className='thumb-image'>
                  <img
                    src={photo.thumbnail}
                    alt={photo.caption || `Thumbnail ${index + 1}`}
                    className='thumbnail-image'
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      <div className={`navigation-wrapper${isRtl ? ' left-arrow' : ' right-arrow'}`}>
        <span
          className={`navigation next${currentImageNumber + 1 === lightBoxPhotos.length ? ' hidden' : ''}`}
          onClick={handleGoToNext}
        >
          <img src={`/assets/icons/${isRtl ? 'left-arrow-icon.svg' : 'right-arrow-icon.svg'}`} alt='next' />
        </span>
      </div>
    </div>
  )
}

ClassCarousel.propTypes = {
  lightBoxPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      caption: PropTypes.string
    })
  ).isRequired,
  currentImage: PropTypes.number.isRequired,
  closeLightBox: PropTypes.func.isRequired,
  setCurrentImage: PropTypes.func.isRequired,
  currentImageNumber: PropTypes.number.isRequired
}
